<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Job',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    oid: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));
  const b_5962862420439144_style = computed(() => ({
    minHeight: '200px',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5962862420439144_style: b_5962862420439144_style.value,
  }));

  const $$load_resource = async () => {
    return $fn.value.fetch('read_a_job', {
      parameters: { id: props.oid },
      asResource: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5962862420439144_style', nodeId: '5962862420439144' },
    ],
    loaders: { resource: $$load_resource },
    ux: {},
    dataRefs: {
      resource,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5962862420439144_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [
          $fn.value.pgettext('Job|Title', 'Job'),
          resource.value.name,
          resource.value.id,
        ],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_shcxjsnv6_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadSlickgrid_0i18z24aj_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'jobs',
    };
  };

  const act_7315092382398562_closeModal_shcxjsnv6 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_shcxjsnv6_getActionArgs,
    });
  const inst_evh_7315092382398562_close_18gwy5rqy =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_shcxjsnv6,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_18gwy5rqy],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_w804ofa42 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_h09tq0hd6 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_w804ofa42,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_h09tq0hd6],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_nglurnlhj =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_o1nd6q97n =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_nglurnlhj,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_o1nd6q97n],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_okric1jp3 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_7tldgkeld =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_okric1jp3,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_7tldgkeld],
    event: 'reload',
  });
  const act_6466705138206980_closeModal_mhrutmz9o =
    new actionTypes.CloseModalAction({});
  const inst_evh_6466705138206980_deleted_yciaq38ki =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeModal_mhrutmz9o,
      event: 'deleted',
      displayName: 'closeModal',
    });
  const act_6466705138206980_reloadSlickgrid_0i18z24aj =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_6466705138206980_reloadSlickgrid_0i18z24aj_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_koo3aqw1z =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadSlickgrid_0i18z24aj,
      event: 'deleted',
      displayName: 'reloadSlickgrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_yciaq38ki,
      inst_evh_6466705138206980_deleted_koo3aqw1z,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_reloadComponentData_tvmga5tek =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1040167445267876_reload_s3pepglfb =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadComponentData_tvmga5tek,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1040167445267876_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_reload_s3pepglfb],
    event: 'reload',
  });
  const act_2140198529698299_closeComponent_zy0wrr1i6 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_dutn8druw =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_zy0wrr1i6,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_dutn8druw],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_18gwy5rqy,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_h09tq0hd6,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_o1nd6q97n,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_7tldgkeld,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_yciaq38ki,
    inst_evh_6466705138206980_deleted_koo3aqw1z,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_reload_s3pepglfb,
    inst_evh_1040167445267876_reload,
    inst_evh_2140198529698299_clickPreventStop_dutn8druw,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_18gwy5rqy = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_18gwy5rqy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_h09tq0hd6 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_h09tq0hd6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_o1nd6q97n = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_o1nd6q97n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_7tldgkeld = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_7tldgkeld.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_yciaq38ki = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_yciaq38ki.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_koo3aqw1z = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_koo3aqw1z.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_reload_s3pepglfb = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_reload_s3pepglfb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_reload = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_dutn8druw = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_dutn8druw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Job"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <JobHeader
          :item="resource"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </JobHeader>
      </UcHeader>
    </template>
    <div
      class="px-4 py-3 h-100 overflow-hidden"
      :style="b_5962862420439144_style"
    >
      <JobView
        :item="resource"
        v-if="resource"
        @reload="evh_1040167445267876_reload($event, {})"
      >
      </JobView>
    </div>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
